import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { db } from "../config/firebase";
import { groceryItemConverter } from "../config/firestoreConverters";
import { DEFAULT_CATEGORIES, normalizeStandardCategoryKey } from "../constants";
import { appCopy, translateAppCopy } from "../localization/appCopy";
import { type GroceryItem } from "../types/grocery";
import {
  SharedListMutations,
  SharedListMutationDelivery,
  type SharedListAuthority,
  type SharedListMutationState,
} from "../sharedListMutations/sharedListMutations";
import { IndexedDBSharedListMutationStore } from "../sharedListMutations/sharedListMutationStore";
import { executeSharedListMutation } from "../sharedListMutations/firebaseSharedListMutationTransport";
import {
  clearSharedListSessionCache,
  readSharedListSessionCache,
  writeSharedListItemsToSessionCache,
} from "../utils/sharedListSessionCache";
import {
  isRetryableSharedListListenerError,
  isSharedListAccessRevocationError,
  sharedListListenerRetryDelayMs,
} from "../utils/sharedListListenerError";

const emptyItems: GroceryItem[] = [];
const mutationDelivery = new SharedListMutationDelivery();

const formatFirebaseError = (error: unknown): string =>
  error instanceof Error
    ? `Error: ${error.message}`
    : "Unexpected error occurred";
const categoriesForItems = (items: GroceryItem[]): string[] =>
  Array.from(
    new Set(
      items.map(
        (item) => normalizeStandardCategoryKey(item.category) || item.category,
      ),
    ),
  );

export const useSharedListItems = (
  listId?: string,
  authority: SharedListAuthority = {},
) => {
  const { t } = useTranslation();
  const initialItems = listId
    ? readSharedListSessionCache(listId)?.items
    : undefined;
  const [projection, setProjection] = useState<
    SharedListMutationState & { listId?: string }
  >({
    listId,
    items: initialItems ?? [],
    queuedOperationsCount: 0,
    isSyncing: false,
    mutationError: null,
  });
  const [loading, setLoading] = useState(initialItems === undefined);
  const [error, setError] = useState<string | null>(null);
  const [isUsingSavedData, setIsUsingSavedData] = useState(
    initialItems !== undefined,
  );
  const hasUsableItemsRef = useRef(initialItems !== undefined);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const engineRef = useRef<SharedListMutations | undefined>(undefined);
  const authorityRef = useRef(authority);
  authorityRef.current = authority;
  const unavailableMessage = translateAppCopy(
    t,
    appCopy.sharedGroceryList.error.accessUnavailableBody,
  );

  useEffect(() => {
    if (!listId) {
      engineRef.current = undefined;
      setProjection({
        listId,
        items: [],
        queuedOperationsCount: 0,
        isSyncing: false,
        mutationError: null,
      });
      setLoading(false);
      setError(unavailableMessage);
      setIsUsingSavedData(false);
      hasUsableItemsRef.current = false;
      return;
    }
    const cachedItems = readSharedListSessionCache(listId)?.items;
    const engine = new SharedListMutations(
      listId,
      {
        store: new IndexedDBSharedListMutationStore(),
        execute: executeSharedListMutation,
        delivery: mutationDelivery,
      },
      cachedItems ?? [],
      navigator.onLine,
      authorityRef.current,
    );
    engineRef.current = engine;
    const updateProjection = () =>
      setProjection({ listId, ...engine.getSnapshot() });
    const unsubscribeMutations = engine.subscribe(updateProjection);
    updateProjection();
    const updateConnectivity = () => {
      setIsOffline(!navigator.onLine);
      engine.setOnline(navigator.onLine);
    };
    const refreshWhenVisible = () => {
      if (!document.hidden) updateConnectivity();
    };
    const backgroundSync = (event: MessageEvent) => {
      if (event.data?.type === "BACKGROUND_SYNC") updateConnectivity();
    };
    window.addEventListener("online", updateConnectivity);
    window.addEventListener("offline", updateConnectivity);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    navigator.serviceWorker?.addEventListener("message", backgroundSync);
    updateConnectivity();
    setLoading(cachedItems === undefined);
    setError(null);
    setIsUsingSavedData(cachedItems !== undefined);
    hasUsableItemsRef.current = cachedItems !== undefined;
    let revoked = false;
    const itemsQuery = collection(
      db,
      "sharedGroceryLists",
      listId,
      "items",
    ).withConverter(groceryItemConverter);
    let unsubscribe = (): void => undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryAttempt = 0;
    let stopped = false;

    const subscribe = (): void => {
      unsubscribe = onSnapshot(
        itemsQuery,
        { includeMetadataChanges: true },
        (snapshot) => {
          if (stopped || revoked || authorityRef.current.accessUnavailable)
            return;
          const loadedItems = snapshot.docs.map((item) => item.data());
          const isFirestoreCacheSnapshot =
            snapshot.metadata?.fromCache === true;
          if (!isFirestoreCacheSnapshot) retryAttempt = 0;
          if (isFirestoreCacheSnapshot && hasUsableItemsRef.current) {
            // Do not let an empty/incomplete SDK memory-cache snapshot replace a
            // session projection while the browser is waiting for the server.
            setError(null);
            setLoading(false);
            setIsUsingSavedData(true);
            return;
          }
          engine.replaceSnapshot(loadedItems);
          if (!isFirestoreCacheSnapshot) {
            writeSharedListItemsToSessionCache(listId, loadedItems);
          }
          setError(null);
          const hasFirestoreCacheItems = loadedItems.length > 0;
          setLoading(isFirestoreCacheSnapshot && !hasFirestoreCacheItems);
          setIsUsingSavedData(
            isFirestoreCacheSnapshot && hasFirestoreCacheItems,
          );
          hasUsableItemsRef.current =
            !isFirestoreCacheSnapshot || hasFirestoreCacheItems;
        },
        (snapshotError) => {
          if (stopped) return;
          if (isRetryableSharedListListenerError(snapshotError)) {
            setError(null);
            setLoading(!hasUsableItemsRef.current);
            setIsUsingSavedData(hasUsableItemsRef.current);
            const delay = sharedListListenerRetryDelayMs(retryAttempt);
            retryAttempt += 1;
            retryTimer = setTimeout(() => {
              if (stopped) return;
              unsubscribe();
              subscribe();
            }, delay);
            return;
          }
          setError(formatFirebaseError(snapshotError));
          engine.replaceSnapshot([]);
          setLoading(false);
          setIsUsingSavedData(false);
          hasUsableItemsRef.current = false;
          if (isSharedListAccessRevocationError(snapshotError)) {
            revoked = true;
            engine.setAuthority({
              ...authorityRef.current,
              accessUnavailable: true,
            });
            clearSharedListSessionCache(listId);
          }
        },
      );
    };

    subscribe();
    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribe();
      unsubscribeMutations();
      window.removeEventListener("online", updateConnectivity);
      window.removeEventListener("offline", updateConnectivity);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      navigator.serviceWorker?.removeEventListener("message", backgroundSync);
      engine.dispose();
    };
  }, [listId, unavailableMessage]);

  useEffect(() => {
    engineRef.current?.setAuthority(authorityRef.current);
    if (listId && authority.accessUnavailable)
      clearSharedListSessionCache(listId);
  }, [
    listId,
    authority.allowEditing,
    authority.expiresAt,
    authority.accessUnavailable,
    authority.authorityReady,
  ]);

  const currentEngine = useCallback(() => {
    const engine = engineRef.current;
    if (!engine || engine.listId !== listId)
      throw new Error(unavailableMessage);
    return engine;
  }, [listId, unavailableMessage]);
  const addItem = useCallback(
    async (name: string, category: string) =>
      currentEngine().addItem(name, category),
    [currentEngine],
  );
  const toggleItem = useCallback(
    async (id: string, currentStatus: boolean, checkedAt?: Date) =>
      currentEngine().toggleItem(id, currentStatus, checkedAt),
    [currentEngine],
  );
  const deleteItem = useCallback(
    async (id: string) => currentEngine().deleteItem(id),
    [currentEngine],
  );
  const updateItem = useCallback(
    async (id: string, updates: { name?: string; category?: string }) =>
      currentEngine().updateItem(id, updates),
    [currentEngine],
  );
  const items =
    projection.listId === listId &&
    !authority.accessUnavailable &&
    (authority.expiresAt === undefined || authority.expiresAt > Date.now())
      ? projection.items
      : emptyItems;
  const categories = useMemo(
    () =>
      Array.from(
        new Set([...DEFAULT_CATEGORIES, ...categoriesForItems(items)]),
      ),
    [items],
  );
  return {
    items,
    categories,
    defaultCategories: DEFAULT_CATEGORIES,
    loading,
    error,
    isUsingSavedData,
    isOffline,
    queuedOperationsCount:
      projection.listId === listId ? projection.queuedOperationsCount : 0,
    isSyncing: projection.listId === listId && projection.isSyncing,
    mutationError:
      projection.listId === listId ? projection.mutationError : null,
    addItem,
    toggleItem,
    deleteItem,
    updateItem,
  };
};
